import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Receipt, Loader2, Eye, CheckCircle, Clock, DollarSign, Plus, Trash2 } from 'lucide-react';
import { invoicesAPI, contractsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { InvoicePreviewModal } from '../components/InvoicePreviewModal';

// Parse YYYY-MM-DD as a LOCAL date so it doesn't shift back a day in negative-UTC zones
const parseLocal = (iso) => {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  if (m) return new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
};
const fmtDate = (iso) => {
  const d = parseLocal(iso);
  return d ? d.toLocaleDateString() : '';
};

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [addPaymentFor, setAddPaymentFor] = useState(null); // invoice we're recording a payment against
  const [paymentDraft, setPaymentDraft] = useState({ amount: '', date: '', method: '', note: '' });
  const { toast } = useToast();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [invoicesData, contractsData] = await Promise.all([
        invoicesAPI.getAll(),
        contractsAPI.getAll()
      ]);
      setInvoices(invoicesData);
      setContracts(contractsData);
    } catch (error) {
      toast({ title: 'Error loading data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  async function createInvoice(sourceContract) {
    const payload = {
      contract_id: sourceContract.id,
      contract_number: sourceContract.contract_number,
      client_name: sourceContract.client_name,
      client_email: sourceContract.client_email,
      client_phone: sourceContract.client_phone,
      event_name: sourceContract.event_name,
      event_date: sourceContract.event_date,
      subtotal: sourceContract.subtotal || sourceContract.total_amount * 0.85,
      tax_amount: sourceContract.tax_amount || sourceContract.total_amount * 0.13,
      gratuity_amount: sourceContract.gratuity_amount || 0,
      total_amount: sourceContract.total_amount,
    };
    try {
      await invoicesAPI.create(payload);
      toast({ title: 'Invoice created!' });
      fetchData();
      setShowCreateDialog(false);
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Error creating invoice', variant: 'destructive' });
    }
  }

  const updatePayment = async (invoice, type) => {
    try {
      const update = type === 'deposit' 
        ? { deposit_paid: true } 
        : { deposit_paid: true, balance_paid: true };
      await invoicesAPI.update(invoice.id, update);
      toast({ title: `${type === 'deposit' ? 'Deposit' : 'Full payment'} recorded!` });
      fetchData();
    } catch (error) {
      toast({ title: 'Error updating payment', variant: 'destructive' });
    }
  };

  const openAddPayment = (invoice) => {
    const today = new Date().toISOString().slice(0, 10);
    // Prefill amount with the remaining balance so recording "paid in full" is one click
    const suggested = (parseFloat(invoice.balance_due) || 0).toFixed(2);
    setPaymentDraft({ amount: suggested, date: today, method: '', note: '' });
    setAddPaymentFor(invoice);
  };

  const submitPayment = async () => {
    if (!addPaymentFor) return;
    const amt = parseFloat(paymentDraft.amount);
    if (!amt || amt <= 0) {
      toast({ title: 'Enter a payment amount greater than $0', variant: 'destructive' });
      return;
    }
    try {
      await invoicesAPI.addPayment(addPaymentFor.id, {
        amount: amt,
        date: paymentDraft.date || undefined,
        method: paymentDraft.method || undefined,
        note: paymentDraft.note || undefined,
      });
      toast({ title: 'Payment recorded' });
      setAddPaymentFor(null);
      fetchData();
    } catch (error) {
      toast({
        title: 'Could not record payment',
        description: error?.response?.data?.detail || error.message,
        variant: 'destructive',
      });
    }
  };

  const removePayment = async (invoice, paymentId) => {
    if (!window.confirm('Remove this payment?')) return;
    try {
      await invoicesAPI.removePayment(invoice.id, paymentId);
      toast({ title: 'Payment removed' });
      fetchData();
    } catch {
      toast({ title: 'Could not remove payment', variant: 'destructive' });
    }
  };

  const contractsWithoutInvoice = contracts.filter(c => 
    !invoices.some(inv => inv.contract_id === c.id)
  );

  const filteredInvoices = useMemo(() => {
    let list = filter === 'all' ? invoices : invoices.filter(i => i.payment_status === filter);
    const q = (searchTerm || '').trim().toLowerCase();
    if (q) {
      list = list.filter(i =>
        (i.invoice_number || '').toLowerCase().includes(q)
        || (i.client_name || '').toLowerCase().includes(q)
        || (i.event_name || '').toLowerCase().includes(q)
        || (i.client_email || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [invoices, filter, searchTerm]);

  const handleDeleteInvoice = async (invoice) => {
    if (!window.confirm(`Permanently delete ${invoice.invoice_number}? This cannot be undone.`)) return;
    try {
      await invoicesAPI.delete(invoice.id);
      toast({ title: 'Invoice deleted' });
      fetchData();
    } catch {
      toast({ title: 'Could not delete invoice', variant: 'destructive' });
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'partial': return 'bg-blue-100 text-blue-800';
      case 'deposit_paid': return 'bg-blue-100 text-blue-800'; // legacy
      default: return 'bg-red-100 text-red-800';
    }
  };
  const statusLabel = (s) => {
    if (s === 'paid') return 'PAID IN FULL';
    if (s === 'partial') return 'PARTIAL';
    if (s === 'deposit_paid') return 'PARTIAL';  // legacy
    return 'UNPAID';
  };

  if (loading) return <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen flex justify-center items-center"><Loader2 className="animate-spin text-[#1e3a5f]" size={48} /></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen" data-testid="invoices-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f] mb-1">Invoices</h1>
          <p className="text-gray-600">Manage billing and track payments</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white" data-testid="create-invoice-btn">
          <Plus size={20} className="mr-2" />Create Invoice
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <input
          type="text"
          placeholder="Search by invoice #, client, event, or email…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-9 pr-9 py-2 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-[#1e3a5f]"
          data-testid="invoices-search-input"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
        {searchTerm && (
          <button
            type="button"
            onClick={() => setSearchTerm('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
            data-testid="invoices-search-clear"
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-6">
        <TabsList className="bg-white">
          <TabsTrigger value="all" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">All ({invoices.length})</TabsTrigger>
          <TabsTrigger value="unpaid" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">Unpaid</TabsTrigger>
          <TabsTrigger value="partial" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">Partial</TabsTrigger>
          <TabsTrigger value="paid" className="data-[state=active]:bg-green-500 data-[state=active]:text-white">Paid in Full</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 gap-6">
        {filteredInvoices.map((invoice) => (
          <Card key={invoice.id} className="border-none shadow-lg" data-testid={`invoice-${invoice.id}`}>
            <CardHeader className="bg-gradient-to-r from-[#8b7355] to-[#c4a574] text-white">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2"><Receipt size={20} />{invoice.invoice_number}</CardTitle>
                  <p className="text-sm opacity-90">{invoice.event_name}</p>
                </div>
                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(invoice.payment_status)}`}>
                  {statusLabel(invoice.payment_status)}
                </span>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div><p className="text-xs text-gray-600">Client</p><p className="font-semibold text-[#1e3a5f]">{invoice.client_name}</p></div>
                <div><p className="text-xs text-gray-600">Event Date</p><p className="font-semibold text-[#1e3a5f]">{fmtDate(invoice.event_date) || '—'}</p></div>
                <div><p className="text-xs text-gray-600">Due Date</p><p className="font-semibold text-[#1e3a5f]">{fmtDate(invoice.due_date) || 'N/A'}</p></div>
                <div><p className="text-xs text-gray-600">Total</p><p className="font-bold text-xl text-[#1e3a5f]">${(invoice.total_amount || 0).toLocaleString()}</p></div>
              </div>

              {/* Payments made + Balance remaining */}
              <div className="bg-gray-50 p-4 rounded-lg mb-4" data-testid={`invoice-payments-${invoice.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-[#1e3a5f]">Payments Received ({(invoice.payments || []).length})</p>
                  <p className="text-sm text-gray-700">
                    Paid <span className="font-bold text-green-700">${(invoice.amount_paid || 0).toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}</span>
                    <span className="mx-2 text-gray-400">·</span>
                    Balance <span className="font-bold text-[#1e3a5f]">${(invoice.balance_due || 0).toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}</span>
                  </p>
                </div>
                {(invoice.payments || []).length === 0 ? (
                  <p className="text-sm text-gray-500 italic" data-testid={`no-payments-${invoice.id}`}>No payments recorded yet.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-500 text-xs uppercase border-b">
                        <th className="text-left py-1">Date</th>
                        <th className="text-left py-1">Method</th>
                        <th className="text-left py-1">Note</th>
                        <th className="text-right py-1">Amount</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(invoice.payments || []).map((p) => (
                        <tr key={p.id} className="border-b border-gray-100" data-testid={`payment-row-${p.id}`}>
                          <td className="py-1">{fmtDate(p.date)}</td>
                          <td className="py-1 text-gray-700">{p.method || '—'}</td>
                          <td className="py-1 text-gray-500 truncate max-w-xs" title={p.note || ''}>{p.note || ''}</td>
                          <td className="py-1 text-right font-semibold text-green-700">${(p.amount || 0).toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}</td>
                          <td className="py-1 text-right">
                            <button
                              type="button"
                              onClick={() => removePayment(invoice, p.id)}
                              className="text-red-500 hover:text-red-700 text-xs"
                              title="Remove this payment"
                              data-testid={`remove-payment-${p.id}`}
                            >×</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="flex gap-3">
                {invoice.payment_status !== 'paid' && (
                  <Button onClick={() => openAddPayment(invoice)} className="bg-green-500 hover:bg-green-600 text-white" data-testid={`add-payment-${invoice.id}`}>
                    <DollarSign size={16} className="mr-1" />Record Payment
                  </Button>
                )}
                <Button onClick={() => setPreviewInvoice(invoice)} variant="outline" className="border-[#1e3a5f] text-[#1e3a5f]" data-testid={`preview-invoice-${invoice.id}`}>
                  <Eye size={16} className="mr-1" />Preview / Print
                </Button>
                <Button onClick={() => handleDeleteInvoice(invoice)} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 ml-auto" data-testid={`delete-invoice-${invoice.id}`}>
                  <Trash2 size={16} className="mr-1" />Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredInvoices.length === 0 && (
        <div className="text-center py-20"><p className="text-gray-500">No invoices found</p></div>
      )}

      {/* Create Invoice Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Create Invoice from Contract</DialogTitle></DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-3">
            {contractsWithoutInvoice.length > 0 ? contractsWithoutInvoice.map(contract => (
              <div key={contract.id} className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer" onClick={() => createInvoice(contract)}>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-[#1e3a5f]">{contract.event_name}</p>
                    <p className="text-sm text-gray-600">{contract.client_name} • {contract.contract_number}</p>
                  </div>
                  <p className="font-bold text-[#1e3a5f]">${contract.total_amount?.toLocaleString()}</p>
                </div>
              </div>
            )) : <p className="text-center py-8 text-gray-500">All contracts have invoices</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={!!addPaymentFor} onOpenChange={(open) => !open && setAddPaymentFor(null)}>
        <DialogContent className="max-w-md" data-testid="record-payment-dialog">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          {addPaymentFor && (
            <div className="space-y-3">
              <div className="bg-gray-50 rounded p-3 text-sm">
                <p className="text-gray-600">{addPaymentFor.invoice_number} · {addPaymentFor.client_name}</p>
                <p className="text-gray-600">Remaining balance <span className="font-bold text-[#1e3a5f]">${(addPaymentFor.balance_due || 0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span></p>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase">Amount</label>
                <input
                  type="number" min="0" step="0.01"
                  value={paymentDraft.amount}
                  onChange={(e) => setPaymentDraft({ ...paymentDraft, amount: e.target.value })}
                  className="w-full border-2 border-gray-200 rounded px-3 py-2 mt-1 focus:outline-none focus:border-[#1e3a5f]"
                  data-testid="payment-amount-input"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase">Payment Date</label>
                <input
                  type="date"
                  value={paymentDraft.date}
                  onChange={(e) => setPaymentDraft({ ...paymentDraft, date: e.target.value })}
                  className="w-full border-2 border-gray-200 rounded px-3 py-2 mt-1 focus:outline-none focus:border-[#1e3a5f]"
                  data-testid="payment-date-input"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase">Method</label>
                <select
                  value={paymentDraft.method}
                  onChange={(e) => setPaymentDraft({ ...paymentDraft, method: e.target.value })}
                  className="w-full border-2 border-gray-200 rounded px-3 py-2 mt-1 focus:outline-none focus:border-[#1e3a5f] bg-white"
                  data-testid="payment-method-input"
                >
                  <option value="">Select method (optional)</option>
                  <option value="E-Transfer">E-Transfer</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Cash">Cash</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Debit">Debit</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase">Note</label>
                <input
                  type="text" placeholder="e.g. Cheque #1234"
                  value={paymentDraft.note}
                  onChange={(e) => setPaymentDraft({ ...paymentDraft, note: e.target.value })}
                  className="w-full border-2 border-gray-200 rounded px-3 py-2 mt-1 focus:outline-none focus:border-[#1e3a5f]"
                  data-testid="payment-note-input"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={submitPayment} className="flex-1 bg-green-500 hover:bg-green-600 text-white" data-testid="submit-payment-btn">
                  Save Payment
                </Button>
                <Button onClick={() => setAddPaymentFor(null)} variant="outline" data-testid="cancel-payment-btn">Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invoice preview / print / download modal */}
      <InvoicePreviewModal
        open={!!previewInvoice}
        onClose={() => setPreviewInvoice(null)}
        invoice={previewInvoice}
        contract={previewInvoice ? contracts.find(c => c.id === previewInvoice.contract_id) : null}
      />
    </div>
  );
};

export default Invoices;
